import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import AdminDialog from "../../components/admin/AdminDialog";
import ImageUploader from "../../components/common/ImageUploader";

const slugify = (s) =>
  (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const emptyForm = {
  name: "",
  slug: "",
  description: "",
  logo_url: "",
  banner_url: "",
  category_slugs: [],
  is_active: true,
  is_featured: false,
  order: 0,
};

export default function AdminBrands() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [categoryInput, setCategoryInput] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/brands");
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load brands");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setCategoryInput("");
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (b) => {
    setForm({
      name: b.name || "",
      slug: b.slug || "",
      description: b.description || "",
      logo_url: b.logo_url || "",
      banner_url: b.banner_url || "",
      category_slugs: b.category_slugs || [],
      is_active: b.is_active !== false,
      is_featured: !!b.is_featured,
      order: b.order || 0,
    });
    setCategoryInput((b.category_slugs || []).join(", "));
    setEditingId(b.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const slugs = categoryInput
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const payload = {
      ...form,
      slug: form.slug || slugify(form.name),
      category_slugs: slugs,
    };
    try {
      if (editingId) {
        await api.put(`/admin/brands/${editingId}`, payload);
        toast.success("Brand updated");
      } else {
        await api.post("/admin/brands", payload);
        toast.success("Brand created");
      }
      setOpen(false);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
        : detail || err?.message || "Save failed";
      toast.error(msg);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete brand "${b.name}"?`)) return;
    try {
      await api.delete(`/admin/brands/${b.id}`);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-brands">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl">Brands</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">
            Manage the brand catalog products can be tagged with.
          </p>
        </div>
        <button
          onClick={openNew}
          className="kb-btn-primary inline-flex items-center gap-2"
          data-testid="admin-brand-new"
        >
          <Plus size={16} /> New Brand
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[hsl(var(--kb-muted))]">Loading…</div>
      ) : (
        <div className="bg-white border border-[hsl(var(--kb-border))] rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--kb-surface-alt))] text-left">
              <tr>
                <th className="p-3 w-16">Logo</th>
                <th className="p-3">Name</th>
                <th className="p-3">Slug</th>
                <th className="p-3">Categories</th>
                <th className="p-3 w-24">Status</th>
                <th className="p-3 w-28">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-[hsl(var(--kb-muted))]">
                    No brands yet — click "New Brand" to add one.
                  </td>
                </tr>
              )}
              {items.map((b) => (
                <tr key={b.id} className="border-t border-[hsl(var(--kb-border))]" data-testid={`brand-row-${b.slug}`}>
                  <td className="p-3">
                    {b.logo_url ? (
                      <img src={b.logo_url} alt="" className="w-10 h-10 object-contain" />
                    ) : (
                      <div className="w-10 h-10 bg-[hsl(var(--kb-surface-alt))] rounded-sm" />
                    )}
                  </td>
                  <td className="p-3 font-medium">{b.name}</td>
                  <td className="p-3 text-[hsl(var(--kb-muted))]">{b.slug}</td>
                  <td className="p-3 text-xs">
                    {(b.category_slugs || []).join(", ") || <span className="text-[hsl(var(--kb-muted))]">—</span>}
                  </td>
                  <td className="p-3">
                    <span
                      className={`text-xs px-2 py-1 rounded-sm ${
                        b.is_active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {b.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="p-3 flex items-center gap-2">
                    <button
                      className="p-1.5 hover:bg-[hsl(var(--kb-surface-alt))]"
                      onClick={() => openEdit(b)}
                      data-testid={`brand-edit-${b.slug}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      className="p-1.5 hover:bg-red-50 text-red-600"
                      onClick={() => remove(b)}
                      data-testid={`brand-delete-${b.slug}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AdminDialog open={open} onOpenChange={setOpen} title={editingId ? "Edit Brand" : "New Brand"}>
        <form className="space-y-4" onSubmit={submit}>
          <div>
            <label className="overline">Name</label>
            <input
              className="kb-input"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
              data-testid="brand-form-name"
            />
          </div>
          <div>
            <label className="overline">Slug</label>
            <input
              className="kb-input"
              value={form.slug}
              placeholder={slugify(form.name)}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              data-testid="brand-form-slug"
            />
          </div>
          <div>
            <label className="overline">Description</label>
            <textarea
              className="kb-input"
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>
          <div>
            <label className="overline">Logo (square, ideally 512×512)</label>
            <ImageUploader
              value={form.logo_url ? [form.logo_url] : []}
              onChange={(arr) => setForm((f) => ({ ...f, logo_url: arr[0] || "" }))}
              max={1}
              aspect="aspect-square"
              label="Upload logo"
              testid="brand-form-logo"
              cropAspect={1}
            />
          </div>
          <div>
            <label className="overline">Departments / Categories (comma-separated slugs)</label>
            <input
              className="kb-input"
              value={categoryInput}
              placeholder="electronics, home-kitchen"
              onChange={(e) => setCategoryInput(e.target.value)}
              data-testid="brand-form-categories"
            />
            <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-1">
              Enter category or department slugs separated by commas.
            </p>
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_featured}
                onChange={(e) => setForm((f) => ({ ...f, is_featured: e.target.checked }))}
              />
              Featured
            </label>
            <label className="flex items-center gap-2 text-sm">
              <span className="overline">Order</span>
              <input
                type="number"
                className="kb-input w-24"
                value={form.order}
                onChange={(e) => setForm((f) => ({ ...f, order: parseInt(e.target.value || "0", 10) }))}
              />
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="kb-btn-outline" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button type="submit" className="kb-btn-primary" data-testid="brand-form-save">
              {editingId ? "Save" : "Create"}
            </button>
          </div>
        </form>
      </AdminDialog>
    </div>
  );
}
